import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    financeApi,
    PaginatedResponse,
    InvoiceListItem,
    InvoiceDetail,
} from '../../services/financeApi';

interface FinanceInvoicesState {
    list: InvoiceListItem[];
    pagination: PaginatedResponse<InvoiceListItem>['meta']['pagination'] | null;
    customers: PaginatedResponse<InvoiceListItem>['meta']['customers'];
    loadingList: boolean;
    loadingDetail: boolean;
    error: string | null;
    selectedInvoice: InvoiceDetail | null;
    downloading: boolean;
}

const initialState: FinanceInvoicesState = {
    list: [],
    pagination: null,
    customers: [],
    loadingList: false,
    loadingDetail: false,
    error: null,
    selectedInvoice: null,
    downloading: false,
};

export const fetchFinanceInvoices = createAsyncThunk(
    'financeInvoices/fetchList',
    async (params: { customerId?: number; page?: number; perPage?: number } = {}) => {
        return await financeApi.getInvoices(params);
    }
);

export const fetchFinanceInvoice = createAsyncThunk(
    'financeInvoices/fetchDetail',
    async (invoiceId: number) => {
        const response = await financeApi.getInvoice(invoiceId);
        return response.data;
    }
);

export const downloadFinanceInvoice = createAsyncThunk(
    'financeInvoices/download',
    async (invoiceId: number) => {
        const blob = await financeApi.downloadInvoice(invoiceId);
        return { invoiceId, blob };
    }
);

const financeInvoicesSlice = createSlice({
    name: 'financeInvoices',
    initialState,
    reducers: {
        clearFinanceInvoiceError: (state) => {
            state.error = null;
        },
        clearSelectedFinanceInvoice: (state) => {
            state.selectedInvoice = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFinanceInvoices.pending, (state) => {
                state.loadingList = true;
                state.error = null;
            })
            .addCase(fetchFinanceInvoices.fulfilled, (state, action: PayloadAction<PaginatedResponse<InvoiceListItem>>) => {
                state.loadingList = false;
                state.list = action.payload.data;
                state.pagination = action.payload.meta.pagination;
                state.customers = action.payload.meta.customers;
            })
            .addCase(fetchFinanceInvoices.rejected, (state, action) => {
                state.loadingList = false;
                state.error = action.error.message || 'Failed to load invoices';
            });

        builder
            .addCase(fetchFinanceInvoice.pending, (state) => {
                state.loadingDetail = true;
                state.error = null;
            })
            .addCase(fetchFinanceInvoice.fulfilled, (state, action: PayloadAction<InvoiceDetail>) => {
                state.loadingDetail = false;
                state.selectedInvoice = action.payload;
            })
            .addCase(fetchFinanceInvoice.rejected, (state, action) => {
                state.loadingDetail = false;
                state.error = action.error.message || 'Failed to load invoice detail';
            });

        builder
            .addCase(downloadFinanceInvoice.pending, (state) => {
                state.downloading = true;
                state.error = null;
            })
            .addCase(downloadFinanceInvoice.fulfilled, (state) => {
                state.downloading = false;
            })
            .addCase(downloadFinanceInvoice.rejected, (state, action) => {
                state.downloading = false;
                state.error = action.error.message || 'Failed to download invoice';
            });
    },
});

export const { clearFinanceInvoiceError, clearSelectedFinanceInvoice } = financeInvoicesSlice.actions;
export default financeInvoicesSlice.reducer;

